import React from "react";

import { TicketPriority } from "@common/models/TicketPriority.model";
import { TicketStatus } from "@common/models/TicketStatus.model";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { RoutePaths } from "@main/router/config/routePaths";
import { Table } from "@tanstack/react-table";
import CountUp from "react-countup";
import { generatePath, Link } from "react-router-dom";

import { HeaderCell, HeaderLimitedCell } from "./BaseTableStyles";
import { MultiactionsPopover } from "./MultiactionsPopover/MultiactionsPopover";

export function SelectionHeaderCell<TData>({
  title,
  total,
  currentMonthIssues,
  table,
  onDeleteRequest,
  onMarkAsReadRequest,
  onBulkChangePriority,
  onBulkChangeStatus,
  onBulkChangeAssignee,
  onBulkAddNoteSendChat,
  onBulkAddTags,
  onMergeAction,
}: {
  title?: string;
  total?: number;
  currentMonthIssues?: number;
  table: Table<TData>;
  onDeleteRequest: (rows: TData[]) => void;
  onMarkAsReadRequest: (rows: TData[]) => void;
  onBulkChangePriority: (rows: TData[], priority: TicketPriority) => void;
  onBulkChangeStatus: (rows: TData[], status: TicketStatus) => void;
  onBulkChangeAssignee: (rows: TData[], assigneeId: string | null) => void;
  onBulkAddNoteSendChat?: (rows: TData[], message: string, isNote?: boolean) => void;
  onBulkAddTags: (rows: TData[], tags: string[]) => void;
  onMergeAction?: (rows: TData[], mergeRow: TData) => void;
}) {
  const selectedRows = table.getSelectedRowModel().rows;
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  const displayLimits =
    !selectedWorkspace?.is_free &&
    !selectedWorkspace?.has_subscription &&
    !selectedApp?.is_sample &&
    !selectedApp?.is_extension &&
    currentMonthIssues !== undefined &&
    currentMonthIssues !== null;

  return (
    <>
      <HeaderCell style={{ display: "flex", alignItems: "center", gap: "1rem", paddingLeft: "1rem" }}>
        <p>
          {total ? (
            <>
              <CountUp
                end={total}
                duration={1}
              />{" "}
              {total === 1 ? "result" : "results"}
              {displayLimits && (
                <HeaderLimitedCell>
                  {currentMonthIssues > 20 ? 20 : currentMonthIssues}/20 tickets arrived this calendar month for free.{" "}
                  <Link
                    to={generatePath(RoutePaths.ADMINISTRATION_BILLING, {
                      workspaceSlug: selectedWorkspace?.slug || null,
                    })}
                  >
                    Go unlimited
                  </Link>
                </HeaderLimitedCell>
              )}
            </>
          ) : (
            title ?? ""
          )}
        </p>
      </HeaderCell>

      {Boolean(selectedRows.length) && (
        <MultiactionsPopover
          table={table}
          selectedRows={selectedRows}
          onDeleteRequest={onDeleteRequest}
          selectAll={() => table.toggleAllRowsSelected(true)}
          deselectAll={() => table.toggleAllRowsSelected(false)}
          onMarkAsReadRequest={onMarkAsReadRequest}
          onBulkChangePriority={onBulkChangePriority}
          onBulkChangeStatus={onBulkChangeStatus}
          onBulkChangeAssignee={onBulkChangeAssignee}
          onBulkAddNoteSendChat={onBulkAddNoteSendChat}
          onBulkAddTags={onBulkAddTags}
          onMergeAction={onMergeAction}
        />
      )}
    </>
  );
}
